import { createSelector } from "@reduxjs/toolkit";
import type { RootState } from "../store";
import type { GetTemplateFolder } from "./templatesTypes";
import type { TemplateBuilderCategory } from "../../components/Library/data/templateBuilderTreeData";

type FilterMode = 'all' | 'draft' | 'published';

/**
 * Unified function to transform template folders to tree data
 * @param folders - Template folders to transform
 * @param mode - Filter mode: 'all' (draft+published), 'draft' only, or 'published' only
 * @param restrictToDraftFolder - If true (used for draft mode), only include Draft/Drafts folders
 */
export const transformFoldersToTreeData = (
  folders: GetTemplateFolder[] | undefined,
  mode: FilterMode = 'all',
  restrictToDraftFolder: boolean = false,
): TemplateBuilderCategory[] => {
  if (!folders || folders.length === 0) {
    return [];
  }

  // Apply folder restriction for draft mode
  let foldersToProcess = folders;
  if (restrictToDraftFolder) {
    foldersToProcess = folders.filter(
      (folder) =>
        folder.folderName.toLowerCase() === "draft" ||
        folder.folderName.toLowerCase() === "drafts",
    );
  }

  const sortedFolders = [...foldersToProcess].sort((a, b) => {
    const isDraftsA = a.folderName.toLowerCase() === "drafts";
    const isDraftsB = b.folderName.toLowerCase() === "drafts";
    if (isDraftsA) return -1;
    if (isDraftsB) return 1;
    return a.folderName.localeCompare(b.folderName);
  });

  const result = sortedFolders
    .map((folder) => {
      const validTemplates = folder.templates.filter((template) => {
        // Check if template has versions
        if (!template.versions || template.versions.length === 0) {
          return false;
        }

        const templateState = template.templateState.toUpperCase();
        const isActive = template.isActive === "Y";
        const hasNullVersionNumber = template.versions.some(
          (version) => version.templateVersionNumber === null,
        );

        // Apply filtering based on mode
        switch (mode) {
          case 'draft':
            return isActive && templateState === "DRAFT";
          
          case 'published':
            return isActive && templateState === "PUBLISHED" && hasNullVersionNumber;
          
          case 'all':
          default:
            // Include active drafts OR active published with null version
            if (!isActive) return false;
            if (templateState === "DRAFT") return true;
            if (templateState === "PUBLISHED" && hasNullVersionNumber) return true;
            return false;
        }
      });

      const sortedTemplates = [...validTemplates].sort((a, b) =>
        a.templateName.localeCompare(b.templateName),
      );

      return {
        id: folder.folderName,
        label: folder.folderName,
        templates: sortedTemplates.map((template) => ({
          id: template.templateId,
          label: template.templateName,
        })),
      } as const;
    })
    .filter((folder) => folder.templates.length > 0);

  return result;
};

export const selectTemplatesLoading = (state: RootState) =>
  state.templates.loading;

export const selectTemplatesError = (state: RootState) => state.templates.error;

export const selectTemplatesHasFetched = (state: RootState) =>
  state.templates.hasFetched;

// Selector to get category-based cached data
export const selectDataByCategory = (state: RootState) => state.templates.dataByCategory;

// Memoized helper selector to get all template folders across all category caches
export const selectAllTemplateFolders = createSelector(
  [selectDataByCategory],
  (dataByCategory) => {
    const allFolders: GetTemplateFolder[] = [];
    
    // Collect from OPTUM cache
    if (dataByCategory.OPTUM) {
      allFolders.push(...(dataByCategory.OPTUM.globalTemplates || []));
      allFolders.push(...(dataByCategory.OPTUM.platformTemplates || []));
      allFolders.push(...(dataByCategory.OPTUM.clientTemplates || []));
    }
    
    // Collect from PLATFORM cache
    if (dataByCategory.PLATFORM) {
      allFolders.push(...(dataByCategory.PLATFORM.globalTemplates || []));
      allFolders.push(...(dataByCategory.PLATFORM.platformTemplates || []));
      allFolders.push(...(dataByCategory.PLATFORM.clientTemplates || []));
    }
    
    // Collect from CLIENT cache
    if (dataByCategory.CLIENT) {
      allFolders.push(...(dataByCategory.CLIENT.globalTemplates || []));
      allFolders.push(...(dataByCategory.CLIENT.platformTemplates || []));
      allFolders.push(...(dataByCategory.CLIENT.clientTemplates || []));
    }
    
    return allFolders;
  },
);

// Helper selector to find a template by ID across all template categories
export const selectTemplateById = createSelector(
  [selectAllTemplateFolders, (_state: RootState, templateId: string) => templateId],
  (allFolders, templateId) => {
    for (const folder of allFolders) {
      const template = folder.templates.find(
        (t) => t.templateId === templateId,
      );
      if (template) {
        return template;
      }
    }
    return null;
  },
);

// Selector to get template versions mapped by templateId in the format: { id, label, value }
// "Draft" is always included as the first entry so the user can return to the editable draft at any time.
export const selectTemplateVersionsByTemplateId = createSelector(
  [selectAllTemplateFolders],
  (allFolders) => {
    const versionsByTemplateId: Record<string, Array<{ id: string; label: string; value: string }>> = {};

    allFolders.forEach((folder) => {
      folder.templates.forEach((template) => {
        const publishedVersions = (template.versions || [])
          .filter((version) => version.templateVersionNumber !== null)
          .sort((a, b) => (b.templateVersionNumber || 0) - (a.templateVersionNumber || 0)) // Sort descending
          .map((version) => {
            const versionNum = version.templateVersionNumber!;
            const versionLabel = `V${versionNum}`;
            const versionIsActive = version.isActive === "Y";
            const label = versionIsActive ? `${versionLabel}_Active` : versionLabel;
            
            return {
              id: String(versionNum),
              label,
              value: versionLabel,
            };
          });

        // Always prepend "Draft" so users can return to the editable draft version
        const draftEntry = { id: "draft", label: "Draft", value: "Draft" };
        versionsByTemplateId[template.templateId] = [draftEntry, ...publishedVersions];
      });
    });

    return versionsByTemplateId;
  },
);

export const selectVersionIdMapByTemplateId = createSelector(
  [selectAllTemplateFolders],
  (allFolders) => {
    const versionIdMap: Record<string, Record<string, number>> = {};

    allFolders.forEach((folder) => {
      folder.templates.forEach((template) => {
        const idMap: Record<string, number> = {};

        (template.versions || []).forEach((version) => {
          if (version.templateVersionNumber === null) {
            idMap["Draft"] = version.templateVersionId;
          } else {
            idMap[`V${version.templateVersionNumber}`] = version.templateVersionId;
          }
        });

        versionIdMap[template.templateId] = idMap;
      });
    });

    return versionIdMap;
  },
);

/**
 * Selector to get active folders based on current account/client context.
 * Mirrors the tree view logic - returns folders that should be visible in the tree.
 * This centralizes the filtering logic used by both EmailTemplate and BaseTemplateFormModal.
 * Only includes folders that have at least one valid template (same as tree view).
 */
export const selectActiveFolderNames = createSelector(
  [
    selectDataByCategory,
    (state: RootState) => state.metadata.accountId,
    (state: RootState) => state.metadata.clientId,
  ],
  (dataByCategory, accountId, clientId) => {
    // Determine which template folders to show based on account/client context
    const hasAccountId = accountId && accountId !== '';
    const hasClientId = clientId && clientId !== '';
    
    let folders: GetTemplateFolder[] | undefined;
    
    if (hasAccountId && hasClientId) {
      // Show client templates only
      folders = dataByCategory.CLIENT?.clientTemplates;
    } else if (hasAccountId) {
      // Show platform templates only - prefer PLATFORM cache, fallback to CLIENT cache
      const source = dataByCategory.PLATFORM || dataByCategory.CLIENT;
      folders = source?.platformTemplates;
    } else {
      // Show global templates only
      folders = dataByCategory.OPTUM?.globalTemplates;
    }
    
    // Apply the same filtering as transformFoldersToTreeData to only include folders with valid templates
    const treeData = transformFoldersToTreeData(folders);
    return treeData.map((category) => category.label);
  },
);

